/**
 * FE-4577 — the client's mirror of the branch's design system.
 *
 * THE INVARIANT THAT MAKES THIS SAFE:
 *
 *   `h` is non-`none` only when the mirror is authoritative.
 *
 * A response can omit a block only if we sent a hash claiming to hold it, and we
 * can only send such a hash once hydration has settled. So a payload normalized
 * against an unhydrated mirror necessarily carried all its blocks. Correctness
 * therefore does NOT depend on boot ordering — awaiting hydration at boot is
 * purely an optimisation that avoids one wasted full delivery per session.
 *
 * BODIES ARE STORED RAW. `resolveDesignTokens` substitutes `{{_s.*}}`/`{{_c.*}}`
 * INTO class and template bodies; persisting those would bake one theme's values
 * into the store, and the backend will never re-send a class it believes we
 * hold, so a theme switch would render stale for ever. Substitution happens
 * after the merge — see `resolve.ts`.
 *
 * Nothing exported here throws or rejects.
 */

import {
  FDS_NONE,
  computeFdsHash,
  readFdsResponse,
  serializeFdsRequest,
} from './protocol';
import { readRecord, writeRecord } from './idb';

const SCHEMA = 1;
const PERSIST_DEBOUNCE_MS = 50;
/** How long a request will wait for hydration before giving up and sending `h=none`. */
const HYDRATE_BUDGET_MS = 1500;
/** Beyond this the record is not worth a quota error; the session goes memory-only. */
const MAX_PERSISTED_BYTES = 4 * 1024 * 1024;

export type DesignSystemState = {
  /** RAW class bodies, keyed BY external_id (the key IS the id). */
  classes: Record<string, any>;
  /** RAW template bodies, keyed BY external_id. */
  templates: Record<string, any>;
  /** RAW theme variable maps, by theme NAME. Accumulates: several may be held. */
  themes: Record<string, Record<string, unknown>>;
  /**
   * Styles delivered without a theme name. Renderable, but never advertised in
   * `th=` and never counted in the hash's `s:` — claiming a theme name we
   * invented would tell the backend we hold something we do not. The cost is
   * that styles keep being re-delivered until a real name is on the wire.
   */
  unnamedTheme: Record<string, unknown> | null;
  /**
   * The `{{_c.*}}` token map. Named `configTokens`, never `config`: this repo
   * already carries a bug comment about the two meanings of `config` colliding
   * (`normalizeAppData.ts:85-94`) and a third collision site is not wanted.
   */
  configTokens: Record<string, unknown> | null;
  activeTheme: string | null;
  /** The `t` mark. Opaque — stored and returned verbatim. */
  mark: string | null;
  /**
   * Bumped ONLY when the four content buckets change. The mark changes on every
   * response and bumps NOTHING — a single counter covering both would invalidate
   * the substitution memo on every response and defeat the whole feature.
   */
  contentRevision: number;
};

type Status = 'idle' | 'ready' | 'degraded';

export type IngestInput = {
  /** The raw `Fds` string, or null when the response carried none at all. */
  fds: string | null;
  classes?: Record<string, any>;
  templates?: Record<string, any>;
  styles?: Record<string, unknown>;
  configTokens?: Record<string, unknown>;
  themeName?: string | null;
};

const emptyState = (): DesignSystemState => ({
  classes: {},
  templates: {},
  themes: {},
  unnamedTheme: null,
  configTokens: null,
  activeTheme: null,
  mark: null,
  contentRevision: 0,
});

const isPlainObject = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

let state: DesignSystemState = emptyState();
let status: Status = 'idle';
/** False when storage is unusable — the session still works, in memory only. */
let persistable = false;
let hydrationPromise: Promise<Status> | null = null;
let persistTimer: ReturnType<typeof setTimeout> | null = null;
let hashCache: { revision: number; promise: Promise<string> } | null = null;
let themeChangeListener: (() => void) | null = null;

/**
 * Notified when the active theme changes AWAY from a previously known one (the
 * first theme a session learns is not a change). Registered at boot rather than
 * called directly so this module stays free of a dependency on the screen cache.
 */
export const setThemeChangeListener = (listener: (() => void) | null): void => {
  themeChangeListener = listener;
};

const noteThemeChange = (previous: string | null, next: string | null): void => {
  if (!previous || previous === next) return;
  try {
    themeChangeListener?.();
  } catch (error) {
    console.warn('[design-system] theme-change listener failed:', error);
  }
};

/**
 * The kill switch. `?nofds` or `window.__FDS_DISABLED__` forces `h=none` and
 * disables every write, reverting to the pre-FE-4577 behaviour without a
 * rollback — and the degraded path is one we need to be correct anyway.
 */
const isDisabled = (): boolean => {
  try {
    if (typeof window === 'undefined') return true;
    // eslint-disable-next-line no-underscore-dangle
    if ((window as any).__FDS_DISABLED__) return true;
    return new URLSearchParams(window.location.search).has('nofds');
  } catch (error) {
    return true;
  }
};

/**
 * Keyed by the public address. The branch is encoded in the HOST
 * (`{project}--{branch}.fractal.cc`), and `config.data.public_url` has not
 * arrived yet at boot, whereas the host is available synchronously.
 */
const storageKey = (): string => `ds:v${SCHEMA}:${window.location.host}`;

const persistNow = async (): Promise<void> => {
  if (!persistable) return;
  const record = { schema: SCHEMA, host: window.location.host, savedAt: Date.now(), state };
  let serializedSize = 0;
  try {
    serializedSize = JSON.stringify(record).length;
  } catch (error) {
    persistable = false;
    return;
  }
  if (serializedSize > MAX_PERSISTED_BYTES) {
    console.warn('[design-system] record too large to persist; continuing in memory only');
    persistable = false;
    return;
  }
  const ok = await writeRecord(storageKey(), record);
  if (!ok) persistable = false;
};

/**
 * Single-flight, trailing-debounced. We always write the COMPLETE current state,
 * so last-write-wins is correct and no queue or ordering logic is needed.
 */
const schedulePersist = (): void => {
  if (!persistable) return;
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    persistTimer = null;
    // Never rejects; nothing to await.
    persistNow();
  }, PERSIST_DEBOUNCE_MS);
};

const adopt = (stored: Record<string, any>): void => {
  state = {
    ...emptyState(),
    classes: isPlainObject(stored.classes) ? stored.classes : {},
    templates: isPlainObject(stored.templates) ? stored.templates : {},
    themes: isPlainObject(stored.themes) ? stored.themes : {},
    unnamedTheme: isPlainObject(stored.unnamedTheme) ? stored.unnamedTheme : null,
    configTokens: isPlainObject(stored.configTokens) ? stored.configTokens : null,
    activeTheme: typeof stored.activeTheme === 'string' ? stored.activeTheme : null,
    mark: typeof stored.mark === 'string' ? stored.mark : null,
    contentRevision: 1,
  };
};

/**
 * Idempotent and never rejects. Resolves `'ready'` (the mirror is authoritative,
 * whether or not it is empty) or `'degraded'` (we hold nothing and claim
 * nothing).
 *
 * `enabled` is injected rather than read from `req.isFrameMode()` to keep this
 * module free of a cycle: `req.ts` imports the header getter from here. In frame
 * mode the schema arrives by postMessage from the editor as an unsaved working
 * copy — persisting that as the branch's authoritative design system would be
 * data poisoning, so frame mode is degraded by policy.
 */
export const hydrateDesignSystem = (options?: { enabled?: boolean }): Promise<Status> => {
  if (hydrationPromise) return hydrationPromise;

  hydrationPromise = (async (): Promise<Status> => {
    if (options?.enabled === false || isDisabled()) {
      status = 'degraded';
      return status;
    }
    const stored = await readRecord(storageKey());
    if (stored === undefined) {
      // Storage unusable (private window, blocked). The mirror still works for
      // this session; it simply starts empty and is not written back.
      persistable = false;
      status = 'ready';
      return status;
    }
    persistable = true;
    if (stored && stored.schema === SCHEMA && stored.host === window.location.host
      && isPlainObject(stored.state)) {
      adopt(stored.state);
    }
    status = 'ready';
    return status;
  })().catch((error) => {
    console.warn('[design-system] hydration failed; continuing without a store:', error);
    status = 'degraded';
    return status;
  });

  return hydrationPromise;
};

/**
 * The live mirror. Read-only by contract — callers must not mutate it.
 * Returned by reference, not copied: `resolve.ts` memoises on object identity.
 */
export const readDesignSystem = (): DesignSystemState => state;

export const isDesignSystemReady = (): boolean => status === 'ready';

const holdsNothing = (): boolean =>
  Object.keys(state.classes).length === 0
  && Object.keys(state.templates).length === 0
  && Object.keys(state.themes).length === 0
  && state.configTokens === null;

/**
 * True once the backend has actually spoken the protocol — it has sent at least
 * one `Fds`, or we are holding bodies it delivered.
 *
 * Distinct from `isDesignSystemReady`, which only says the mirror is
 * trustworthy: against a backend without FE-4576 the mirror is perfectly ready
 * and permanently empty. Anything that DISCARDS data on the promise that the
 * store can give it back — `toCacheableBody` — must gate on this one, or a
 * renderer deployed ahead of the engine caches screens stripped of a design
 * system nothing can restore.
 */
export const isDesignSystemActive = (): boolean =>
  status === 'ready' && (state.mark !== null || !holdsNothing());

/** `r=1` — drop everything held. Also used for diagnostics. */
export const resetDesignSystem = (): void => {
  const { mark } = state;
  state = emptyState();
  state.mark = mark;
  state.contentRevision += 1;
  hashCache = null;
};

/**
 * Applies one delivery. Synchronous, so the very next line of the trust boundary
 * can read the merged result; persistence is scheduled, not awaited.
 *
 * Implements the protocol rules verbatim:
 *   2/8 — no `Fds` at all ⇒ an older backend or the SSR shell: touch nothing.
 *   3   — `r=1` clears first, then writes.
 *   1   — the mark is stored from EVERY response, including empty ones.
 *   5   — an absent block means "you already have it", never a deletion.
 *   7   — a duplicate delivery overwrites by id and is harmless.
 */
export const ingestDelivery = (input: IngestInput): void => {
  if (status !== 'ready') return;

  const parsed = readFdsResponse(input.fds);
  if (!parsed) return; // rules 2 and 8

  if (parsed.reset) resetDesignSystem(); // rule 3

  let contentChanged = false;

  if (isPlainObject(input.classes) && Object.keys(input.classes).length > 0) {
    Object.assign(state.classes, input.classes); // rules 5 and 7
    contentChanged = true;
  }
  if (isPlainObject(input.templates) && Object.keys(input.templates).length > 0) {
    Object.assign(state.templates, input.templates);
    contentChanged = true;
  }

  const themeName = typeof input.themeName === 'string' && input.themeName.trim()
    ? input.themeName.trim()
    : null;
  if (themeName) {
    const previousTheme = state.activeTheme;
    state.activeTheme = themeName;
    noteThemeChange(previousTheme, themeName);
  }

  if (isPlainObject(input.styles) && Object.keys(input.styles).length > 0) {
    if (themeName) {
      state.themes[themeName] = input.styles;
      contentChanged = true;
    } else {
      // Renderable but unadvertised — see `unnamedTheme` above.
      state.unnamedTheme = input.styles;
      contentChanged = true;
    }
  }

  if (isPlainObject(input.configTokens) && Object.keys(input.configTokens).length > 0) {
    state.configTokens = input.configTokens;
    contentChanged = true;
  }

  state.mark = parsed.mark; // rule 1 — always, even when nothing was delivered

  if (contentChanged) {
    state.contentRevision += 1;
    hashCache = null;
  }

  schedulePersist();
};

export const setActiveTheme = (name: string | null): void => {
  const next = typeof name === 'string' && name.trim() ? name.trim() : null;
  if (state.activeTheme === next) return;
  const previousTheme = state.activeTheme;
  state.activeTheme = next;
  noteThemeChange(previousTheme, next);
  schedulePersist();
};

const hashOfMirror = (): Promise<string> => {
  if (hashCache && hashCache.revision === state.contentRevision) return hashCache.promise;
  const promise = computeFdsHash({
    classIds: Object.keys(state.classes),
    templateIds: Object.keys(state.templates),
    themeNames: Object.keys(state.themes),
    hasConfig: state.configTokens !== null,
  });
  hashCache = { revision: state.contentRevision, promise };
  return promise;
};

const NONE_HEADER = serializeFdsRequest({ hash: FDS_NONE, mark: null, themes: [] });

/**
 * The `Fds` request header. Never rejects; degrades to `h=none`, which is simply
 * today's behaviour — the backend delivers everything.
 *
 * Waits (bounded) for hydration rather than racing it, so the first request of a
 * session can already claim what the previous one stored.
 */
export const getFdsRequestHeader = async (): Promise<string> => {
  try {
    if (isDisabled()) return NONE_HEADER;

    if (status === 'idle') {
      await Promise.race([
        hydrateDesignSystem(),
        new Promise((resolve) => { setTimeout(resolve, HYDRATE_BUDGET_MS); }),
      ]);
    }
    if (status !== 'ready') return NONE_HEADER;

    // An empty-but-usable store may send either `h=none` or the empty-store
    // hash; the backend handles both, and `none` is the clearer signal.
    if (holdsNothing()) return NONE_HEADER;

    const hash = await hashOfMirror();
    if (hash === FDS_NONE) return NONE_HEADER;

    return serializeFdsRequest({
      hash,
      mark: state.mark,
      themes: Object.keys(state.themes),
    });
  } catch (error) {
    return NONE_HEADER;
  }
};
